import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";

const EMPTY = { label: "Home", full_name: "", phone: "", pincode: "", city: "", state: "", line1: "", line2: "", is_default: false };

export default function SavedAddresses() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null); // null = not editing; object = add/edit
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pinStatus, setPinStatus] = useState(null);

  const load = async () => setList((await api.get("/addresses")).data);
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onPincode = async (v) => {
    const pin = v.replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, pincode: pin }));
    if (pin.length !== 6) { setPinStatus(null); return; }
    setPinStatus("loading");
    try {
      const data = await (await fetch(`https://api.postalpincode.in/pincode/${pin}`)).json();
      const po = data?.[0]?.Status === "Success" ? data[0].PostOffice?.[0] : null;
      if (po) {
        setForm((f) => ({ ...f, city: (po.Block && po.Block !== "NA" ? po.Block : po.District) || "", state: po.State || "" }));
        setPinStatus("valid");
      } else setPinStatus("invalid");
    } catch { setPinStatus("invalid"); }
  };

  const startAdd = () => { setForm({ ...EMPTY }); setEditId(null); setPinStatus(null); };
  const startEdit = (a) => { setForm({ ...EMPTY, ...a }); setEditId(a.id); setPinStatus(null); };

  const save = async () => {
    for (const k of ["full_name", "phone", "pincode", "city", "state", "line1"]) {
      if (!String(form[k] || "").trim()) return toast.error("Please complete all required fields.");
    }
    setBusy(true);
    try {
      if (editId) await api.put(`/addresses/${editId}`, form);
      else await api.post("/addresses", form);
      toast.success(editId ? "Address updated" : "Address saved");
      setForm(null); setEditId(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/addresses/${id}`);
    toast.success("Address removed");
    load();
  };

  return (
    <div className="max-w-[1000px] mx-auto px-6 md:px-12 py-12">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Your Account</p>
          <h1 className="font-serif text-6xl tracking-tighter">Saved Addresses.</h1>
        </div>
        {!form && (
          <button onClick={startAdd} className="bg-foreground text-background px-5 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))]">
            + Add Address
          </button>
        )}
      </div>

      {form && (
        <div className="mt-8 border border-foreground p-6 md:p-8 space-y-4 max-w-2xl">
          <div className="grid grid-cols-2 gap-4">
            <L label="Label"><input value={form.label} onChange={set("label")} placeholder="Home / Work" className="underline-input" /></L>
            <L label="Full Name" req><input value={form.full_name} onChange={set("full_name")} className="underline-input" /></L>
          </div>
          <L label="Mobile Number" req><input value={form.phone} onChange={set("phone")} inputMode="numeric" className="underline-input" /></L>
          <L label="PIN Code" req hint={pinStatus === "valid" ? "✓ Valid" : pinStatus === "invalid" ? "Not found" : pinStatus === "loading" ? "Checking…" : ""}>
            <input value={form.pincode} onChange={(e) => onPincode(e.target.value)} inputMode="numeric" className="underline-input" />
          </L>
          <div className="grid grid-cols-2 gap-4">
            <L label="City" req><input value={form.city} onChange={set("city")} className="underline-input" /></L>
            <L label="State" req><input value={form.state} onChange={set("state")} className="underline-input" /></L>
          </div>
          <L label="Address Line 1" req><input value={form.line1} onChange={set("line1")} placeholder="House/Flat No., Building, Street" className="underline-input" /></L>
          <L label="Address Line 2"><input value={form.line2} onChange={set("line2")} placeholder="Area, Landmark (optional)" className="underline-input" /></L>
          <label className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest cursor-pointer">
            <input type="checkbox" checked={form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} />
            Set as default
          </label>
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={busy} className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground disabled:opacity-50">
              {busy ? "Saving…" : editId ? "Update Address" : "Save Address"}
            </button>
            <button onClick={() => { setForm(null); setEditId(null); }} className="border border-foreground px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-10 space-y-4">
        {list.length === 0 && !form && (
          <p className="font-serif text-2xl text-muted-foreground">No saved addresses yet.</p>
        )}
        {list.map((a) => (
          <div key={a.id} className="border border-foreground p-5 flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {a.label}{a.is_default && <span className="text-[hsl(var(--accent))]"> · Default</span>}
              </p>
              <p className="font-serif text-xl mt-1">{a.full_name}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} — {a.pincode}
              </p>
              <p className="font-mono text-[11px] text-muted-foreground mt-1">{a.phone}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => startEdit(a)} aria-label="edit" className="p-2 hover:text-[hsl(var(--accent))]"><Pencil size={16} strokeWidth={1.5} /></button>
              <button onClick={() => del(a.id)} aria-label="delete" className="p-2 hover:text-destructive"><Trash2 size={16} strokeWidth={1.5} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function L({ label, req, hint, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}{req && <span className="text-[hsl(var(--accent))]"> *</span>}
        </p>
        {hint && <span className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
